/**
 * The contact profile: one contact read whole, with the thing to do about them
 * first and everything else in a fixed place.
 *
 * Replaces the four equal scrolling columns, in which every section was the
 * same kind of box and nothing led. Reading order is now the order a desk asks
 * its questions: who is this and what do I do (hero, relationship health, the
 * one suggested action), how do I reach them (left), what has happened (centre),
 * and what is their record and who else is there (right).
 *
 * Everything shown is read from the contact the page already holds, plus three
 * reads the old window also made: the contact's activity trail (notes and
 * merges), the enrichment trail (where each fact came from) and the people at
 * the firm. The window owns the mutations; this component only draws and
 * hands the window the two things it can ask for (re-enrich, follow a link).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

import { crmErrorStatus, crmService, partyTypeFor, type CrmContact } from '@/services/crm'
import { crmRelationshipService } from '@/services/crmRelationships'
import {
  ActivityEntry,
  BrokerIntelligenceSection,
  NewsSection,
  NotesSection,
  SponsorFinancialsSection,
} from './ProfileSections'
import { DealsByMonth, Timeline } from './ProfileActivity'
import { ProfileHero, NextBestActionCard, RelationshipHealth } from './ProfileHero'
import { HowToReach, LinkedRecords, OtherAddresses, RecordDetails } from './ProfileReach'
import { PeopleAtFirm, TrackRecordCard, WhatWeKnow } from './ProfileRight'
import {
  addressGroups,
  boardAction,
  buildEvents,
  dealFacts,
  dealsByMonth,
  isScored,
  mergedFrom,
  nextAction,
  quietGap,
  readError,
  relationshipFacts,
  trackRecord,
} from './profileFacts'
import { Card, FOCUS } from './profileUi'

/** A read the reader's role cannot make is not made again; anything else gets one retry. */
const retryUnlessDenied = (failures: number, error: Error) => crmErrorStatus(error) !== 403 && failures < 1

/** How much of the activity trail the profile reads: notes, merges, and the
 *  record's own history. */
export const ACTIVITY_LIMIT = 100

/** A quiet block: a section from the older renderers, stripped of their own frame. */
function Legacy({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-stone-800/80 pt-3 [&>section]:!border-0 [&>section]:!px-0 [&>section]:!py-0">{children}</div>
  )
}

type ProfileTab = 'overview' | 'people' | 'intelligence' | 'history'

/**
 * The sections below the hero, one tab at a time.
 *
 * Every section on one page ran the window to about twice its height (Edward,
 * 30 Oct 2026: the right column alone, track record to news, was taller than
 * the screen). Overview holds what a desk reads a contact by; the rest are a
 * tab away, so the window itself never scrolls on a desktop. Every panel stays
 * mounted, so a filter or an open card survives a tab change.
 */
function SectionTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: ProfileTab; label: string }[]
  active: ProfileTab
  onChange: (tab: ProfileTab) => void
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const at = tabs.findIndex((t) => t.id === active)
    const next = tabs[(at + step + tabs.length) % tabs.length]
    onChange(next.id)
    document.getElementById(`contact-tab-${next.id}`)?.focus()
  }
  return (
    <div role="tablist" aria-label="Contact sections" className="flex items-center gap-1 border-b border-stone-800">
      {tabs.map((t) => {
        const selected = t.id === active
        return (
          <button
            key={t.id}
            id={`contact-tab-${t.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`contact-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            onKeyDown={onKeyDown}
            className={clsx(
              '-mb-px rounded-t border-b-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em]',
              FOCUS,
              selected ? 'border-amber-300 text-amber-200' : 'border-transparent text-stone-500 hover:text-stone-200',
            )}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

function TabPanel({ id, active, className, children }: { id: ProfileTab; active: ProfileTab; className?: string; children: ReactNode }) {
  return (
    <div
      role="tabpanel"
      id={`contact-panel-${id}`}
      aria-labelledby={`contact-tab-${id}`}
      hidden={id !== active}
      className={clsx('min-h-0 xl:h-full', className)}
    >
      {children}
    </div>
  )
}

export interface ContactProfileProps {
  contact: CrmContact
  /** "Director · Cushman & Wakefield · Jersey City, NJ". */
  subtitle: string
  /** Chips that act on the record (Needs review + Confirm, Retired + Restore). */
  chips: ReactNode
  /** The Open deal control, or null when the contact has no deal. */
  dealsMenu: ReactNode
  onReenrich: () => void
  reenrichPending: boolean
  onOpenContact?: (id: string) => void
  /** The merge-log link leaves the page, so the window closes first. */
  onNavigate: () => void
  /** The clock; fixed by tests. */
  now?: Date
}

export default function ContactProfile({
  contact,
  subtitle,
  chips,
  dealsMenu,
  onReenrich,
  reenrichPending,
  onOpenContact,
  onNavigate,
  now: nowProp,
}: ContactProfileProps) {
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  const notesRef = useRef<HTMLInputElement>(null)
  const partyType = partyTypeFor(contact.id)

  const activity = useQuery({
    queryKey: ['crm', 'activity', 'contact', contact.id, ACTIVITY_LIMIT],
    queryFn: () => crmService.getContactActivity(contact.id, ACTIVITY_LIMIT),
    retry: retryUnlessDenied,
  })
  const trail = useQuery({
    queryKey: ['crm', 'enrichment', partyType, contact.id],
    queryFn: () => crmService.getEnrichmentHistory(partyType, contact.id),
    retry: retryUnlessDenied,
  })

  // The board's stage and score for this contact. Its endpoint belongs to the
  // relationships work; where it is not deployed the read fails and the profile
  // falls back to the plain counts and its own stated rule.
  const board = useQuery({
    // The list page's own key, so opening a profile from it reads what it already holds.
    queryKey: ['crm', 'contacts', 'relationships'],
    queryFn: crmRelationshipService.get,
    enabled: isScored(contact),
    retry: false,
    staleTime: 60_000,
  })

  const trailItems = useMemo(() => activity.data?.items ?? [], [activity.data])
  const scored = isScored(contact)
  const groups = useMemo(() => addressGroups(contact), [contact])
  const deals = useMemo(() => dealFacts(contact), [contact])
  const events = useMemo(() => buildEvents(contact, trailItems), [contact, trailItems])
  const facts = useMemo(() => relationshipFacts(contact, events, now), [contact, events, now])
  const months = useMemo(() => dealsByMonth(deals, now), [deals, now])
  const record = useMemo(() => trackRecord(deals), [deals])
  const gap = quietGap(facts)
  const relationship = useMemo(() => {
    const row = board.data?.rows.find((r) => r.id === contact.id)
    return row && board.data ? { row, definitions: board.data.definitions } : null
  }, [board.data, contact.id])
  const merged = useMemo(() => mergedFrom(contact.id, trailItems), [contact.id, trailItems])
  const entity = contact.kind === 'organisation' || Boolean(contact.isEntity)
  const first = contact.firstName?.trim()
  const emailLabel = !entity && first ? `Email ${first}` : 'Email'
  // One source for the suggestion: the board's when it has answered (so this
  // card and its "Going quiet" list agree), this file's own rule only where
  // the board is not there. While the board is still answering there is no card
  // rather than one that may change.
  const action = !scored
    ? null
    : board.isSuccess
      ? relationship
        ? boardAction(relationship.row, relationship.definitions, deals, first || contact.displayName)
        : null
      : board.isError
        ? nextAction(deals, now)
        : null

  const [tab, setTab] = useState<ProfileTab>('overview')
  // The note box is on Overview: Log a note shows that tab, then focuses the
  // box once it is on screen (a hidden input cannot take focus).
  const [noteRequest, setNoteRequest] = useState(0)
  useEffect(() => {
    if (!noteRequest) return
    const input = notesRef.current
    if (!input) return
    input.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    input.focus()
  }, [noteRequest])
  const logNote = () => {
    setTab('overview')
    setNoteRequest((n) => n + 1)
  }

  const isBrokerPerson = contact.companyType === 'Broker' && partyType !== 'broker_firm'
  const isSponsor = partyType === 'sponsor'
  const tabs: { id: ProfileTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'people', label: 'People' },
    // News alone is what a contact with no type-specific intelligence has.
    { id: 'intelligence', label: isBrokerPerson || isSponsor ? 'Intelligence' : 'News' },
    { id: 'history', label: `History${activity.isSuccess ? ` (${trailItems.length})` : ''}` },
  ]
  // Each column scrolls on its own when its cards outgrow the window; the window does not.
  const column = 'min-h-0 space-y-4 xl:h-full xl:overflow-y-auto xl:pr-1 scrollbar-thin'

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin xl:overflow-hidden" data-testid="contact-profile">
      <div className="shrink-0 space-y-4 px-5 pt-5">
        <div className={clsx('grid items-center gap-5', scored && 'xl:grid-cols-[minmax(0,1fr)_minmax(440px,560px)]')}>
          <ProfileHero
            contact={contact}
            subtitle={subtitle}
            groups={groups}
            stage={relationship?.row.stage}
            chips={chips}
            dealsMenu={dealsMenu}
            onLogNote={logNote}
          />
          {scored ? <RelationshipHealth facts={facts} relationship={relationship} organization={entity} /> : null}
        </div>

        {action ? <NextBestActionCard action={action} groups={groups} emailLabel={emailLabel} /> : null}

        <SectionTabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <div className="min-h-0 flex-1 px-5 pb-5 pt-4">
        <TabPanel id="overview" active={tab}>
          <div className="grid gap-5 lg:grid-cols-[minmax(250px,300px)_minmax(0,1fr)] xl:h-full xl:grid-cols-[minmax(250px,300px)_minmax(0,1fr)_minmax(280px,340px)]">
            <div className={column}>
              <HowToReach contact={contact} groups={groups} />
              <OtherAddresses groups={groups} />
              <LinkedRecords contact={contact} merged={merged} onOpenContact={onOpenContact} onNavigate={onNavigate} />
              <Legacy>
                <NotesSection contact={contact} inputRef={notesRef} />
              </Legacy>
              <RecordDetails contact={contact} />
            </div>

            <div className={clsx(column, 'min-w-0')}>
              {scored && deals.length > 0 ? (
                <DealsByMonth
                  deals={deals}
                  buckets={months.buckets}
                  undated={months.undated}
                  earlier={months.earlier}
                  facts={facts}
                  showFunded={record.funded !== null}
                  now={now}
                />
              ) : null}
              <Timeline
                events={events}
                gap={gap}
                notes={{
                  pending: activity.isPending,
                  error: activity.isError ? readError(activity.error, 'The activity trail could not be read.') : null,
                }}
              />
            </div>

            <div className={clsx(column, 'lg:col-span-2 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0 xl:col-span-1 xl:block xl:space-y-4')}>
              {scored ? <TrackRecordCard record={record} /> : null}
              <WhatWeKnow contact={contact} trail={trail} onReenrich={onReenrich} reenrichPending={reenrichPending} />
            </div>
          </div>
        </TabPanel>

        <TabPanel id="people" active={tab}>
          <div className="xl:h-full xl:overflow-y-auto scrollbar-thin">
            <PeopleAtFirm contact={contact} onOpenContact={onOpenContact} />
          </div>
        </TabPanel>

        <TabPanel id="intelligence" active={tab}>
          <div className="grid items-start gap-5 xl:h-full xl:grid-cols-2 xl:overflow-y-auto scrollbar-thin">
            {isBrokerPerson ? (
              <Legacy>
                <BrokerIntelligenceSection contact={contact} />
              </Legacy>
            ) : null}
            {isSponsor ? (
              <Legacy>
                <SponsorFinancialsSection contact={contact} />
              </Legacy>
            ) : null}
            <Legacy>
              <NewsSection contact={contact} />
            </Legacy>
          </div>
        </TabPanel>

        <TabPanel id="history" active={tab}>
          <div className="xl:h-full xl:overflow-y-auto scrollbar-thin">
            <Card title="Record history" kicker>
              {activity.isPending ? (
                <p className="flex items-center gap-1.5 text-[11px] text-stone-500">
                  <Loader2 size={11} className="motion-safe:animate-spin" aria-hidden /> Reading the trail…
                </p>
              ) : activity.isError ? (
                <p role="alert" className="text-[11px] text-red-300">
                  {readError(activity.error, 'The activity trail could not be read.')}
                </p>
              ) : trailItems.length === 0 ? (
                <p className="text-[12px] text-stone-500">Nothing recorded for this contact yet.</p>
              ) : (
                <ol className="space-y-1.5 border-l border-amber-400/25">
                  {trailItems.map((entry) => (
                    <ActivityEntry key={entry.id} entry={entry} />
                  ))}
                </ol>
              )}
            </Card>
          </div>
        </TabPanel>
      </div>
    </div>
  )
}
